import { useTranslate } from '@tolgee/react';
import { Server } from 'lincd-server-utils/utils/Server';
import * as React from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { packageName } from '../../../package.js';
import style from './BefriendDrawer.module.css';
import type { BefriendSheetProps } from './BefriendSheet.js';

type Status = 'idle' | 'submitting' | 'thanks' | 'rate_limited' | 'error';

interface Counts {
  totalToday: number;
  totalWorldwide: number;
}

interface ErrorInfo {
  message?: string;
  retryAfterMs?: number;
}

const TRANSIENT_BANNER_MS = 2500;
const MAX_EXPANDED_VIEWPORT_FRACTION = 0.92;
const DRAG_THRESHOLD_PX = 10;

const INITIAL_SNAP = {
  indicator: 26,
  peek: 120,
  full: 360,
};

/**
 * Three snap heights (min / peek / full). Vanilla bottom sheet portaled into
 * TorchOverlay — avoids Vaul's full-height translate model, which kept the
 * card off-screen when snap strings updated from ResizeObserver.
 */
type DrawerState = 'min' | 'peek' | 'full';

interface SnapHeights {
  indicator: number;
  peek: number;
  full: number;
}

function getSafeAreaBottomPx(): number {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(
    '--safe-area-bottom'
  );
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : 0;
}

function heightForState(s: DrawerState, snaps: SnapHeights): number {
  if (s === 'min') return snaps.indicator;
  if (s === 'full') return snaps.full;
  return snaps.peek;
}

function expandCoverForHeight(
  height: number,
  peek: number,
  full: number
): number {
  const span = Math.max(full - peek, 1);
  return Math.min(Math.max((height - peek) / span, 0), 1);
}

export function BefriendDrawer({
  expanded,
  onCollapse,
  onExpand,
  firstExpandThisSession,
  container,
}: BefriendSheetProps) {
  const { t } = useTranslate();
  const [drawerState, setDrawerState] = useState<DrawerState>('peek');
  const [status, setStatus] = useState<Status>('idle');
  const [counts, setCounts] = useState<Counts | null>(null);
  const [errorInfo, setErrorInfo] = useState<ErrorInfo | null>(null);
  const ctaRef = useRef<HTMLButtonElement | null>(null);
  const bannerTimerRef = useRef<number | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const headerRef = useRef<HTMLButtonElement | null>(null);
  const handleRef = useRef<HTMLDivElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const drawerStateRef = useRef<DrawerState>(drawerState);
  const snapHeightsRef = useRef<SnapHeights>(INITIAL_SNAP);
  const [snapHeights, setSnapHeights] = useState<SnapHeights>(INITIAL_SNAP);
  /** True when prompt content is taller than the viewport cap (internal scroll). */
  const [bodyScrollNeeded, setBodyScrollNeeded] = useState(false);
  const [expandCover, setExpandCover] = useState(0);
  const [dragHeightPx, setDragHeightPx] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const isDraggingRef = useRef(false);
  const dragStartYRef = useRef(0);
  const dragStartHeightRef = useRef(0);
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null);
  /** Set on pointerdown when the handle/header was pressed (capture retargets up events). */
  const pointerOnHeaderRef = useRef(false);
  const draggedRef = useRef(false);
  /** Blocks the synthetic click after a drag so Enable buttons underneath don't fire. */
  const suppressClickRef = useRef(false);

  const isExpanded = drawerState === 'full';
  const showBody = expandCover > 0.06;

  const settledHeight =
    dragHeightPx ?? heightForState(drawerState, snapHeights);

  useLayoutEffect(() => {
    drawerStateRef.current = drawerState;
  }, [drawerState]);

  useLayoutEffect(() => {
    const inner = innerRef.current;
    const header = headerRef.current;
    const handle = handleRef.current;
    if (!inner || !header || !handle) return;

    const measure = () => {
      const cap = Math.round(
        window.innerHeight * MAX_EXPANDED_VIEWPORT_FRACTION
      );
      const safeBottom = getSafeAreaBottomPx();
      const handleEnd =
        header.offsetTop + handle.offsetTop + handle.offsetHeight;
      const indicator = Math.min(Math.ceil(handleEnd + 10 + safeBottom), cap);
      const headerHeight = Math.ceil(header.getBoundingClientRect().height);
      // Keep the collapsed header clear of the iOS home-indicator area even
      // when no permission controls are present below it.
      let peekCover = headerHeight + safeBottom;
      if (container) {
        const bottomChrome = container.querySelector(
          '[data-peaceflame-bottom-chrome]'
        );
        if (bottomChrome) {
          const cRect = container.getBoundingClientRect();
          const bRect = bottomChrome.getBoundingClientRect();
          peekCover = Math.max(peekCover, Math.ceil(cRect.bottom - bRect.top));
        }
      }
      const peek = Math.min(peekCover, cap);

      const bodyEl = bodyRef.current;
      let bodyContent = 0;
      if (bodyEl && bodyEl.children.length > 0) {
        bodyEl.classList.add(style.bodyMeasure);
        bodyContent = Array.from(bodyEl.children).reduce(
          (sum, el) => sum + (el as HTMLElement).offsetHeight,
          0
        );
        bodyContent += (bodyEl.children.length - 1) * 14;
        const bodyCs = getComputedStyle(bodyEl);
        bodyContent +=
          (parseFloat(bodyCs.paddingTop) || 0) +
          (parseFloat(bodyCs.paddingBottom) || 0);
        bodyEl.classList.remove(style.bodyMeasure);
      }
      const fullNatural = Math.ceil(headerHeight + bodyContent);
      const full = Math.min(Math.max(fullNatural, peek + 40), cap);

      const next: SnapHeights = { indicator, peek, full };
      snapHeightsRef.current = next;
      setSnapHeights(next);
      setBodyScrollNeeded(fullNatural > cap);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(inner);
    ro.observe(header);
    if (bodyRef.current) {
      ro.observe(bodyRef.current);
    }
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [container]);

  useEffect(() => {
    if (isDragging) return;
    setExpandCover(drawerState === 'full' ? 1 : 0);
  }, [drawerState, isDragging]);

  const prevExpandedRef = useRef(expanded);
  useEffect(() => {
    if (prevExpandedRef.current === expanded) return;
    prevExpandedRef.current = expanded;
    applyState(expanded ? 'full' : 'peek');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  useEffect(() => {
    if (!isExpanded) return;
    const id = window.setTimeout(() => {
      ctaRef.current?.focus();
    }, 120);
    return () => window.clearTimeout(id);
  }, [isExpanded]);

  useEffect(() => {
    return () => {
      if (bannerTimerRef.current !== null) {
        window.clearTimeout(bannerTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const blockGhostClick = (e: MouseEvent) => {
      if (!suppressClickRef.current) return;
      suppressClickRef.current = false;
      e.preventDefault();
      e.stopPropagation();
    };
    document.addEventListener('click', blockGhostClick, true);
    return () => document.removeEventListener('click', blockGhostClick, true);
  }, []);

  const scheduleRevertToIdle = () => {
    if (bannerTimerRef.current !== null) {
      window.clearTimeout(bannerTimerRef.current);
    }
    bannerTimerRef.current = window.setTimeout(() => {
      bannerTimerRef.current = null;
      setStatus('idle');
      setErrorInfo(null);
    }, TRANSIENT_BANNER_MS);
  };

  const handleTap = async () => {
    setStatus('submitting');
    try {
      const result: any = await Server.call(packageName, 'reportBefriend');
      if (result && result.ok) {
        setCounts({
          totalToday: result.totalToday ?? 0,
          totalWorldwide: result.totalWorldwide ?? 0,
        });
        setStatus('thanks');
        scheduleRevertToIdle();
        return;
      }
      if (result && result.error === 'rate_limited') {
        setErrorInfo({ retryAfterMs: result.retryAfterMs });
        setStatus('rate_limited');
        scheduleRevertToIdle();
        return;
      }
      setErrorInfo({ message: result?.error || 'Unknown error' });
      setStatus('error');
      scheduleRevertToIdle();
    } catch (err: any) {
      console.warn('reportBefriend failed:', err);
      setErrorInfo({ message: err?.message ?? 'Network error' });
      setStatus('error');
      scheduleRevertToIdle();
    }
  };

  const applyState = (next: DrawerState) => {
    drawerStateRef.current = next;
    setDrawerState(next);
    setDragHeightPx(null);
    setIsDragging(false);
    isDraggingRef.current = false;
    setExpandCover(next === 'full' ? 1 : 0);
    if (next === 'full') {
      onExpand();
    } else {
      onCollapse();
    }
  };

  const settleFromHeight = (height: number) => {
    const snaps = snapHeightsRef.current;
    const order: DrawerState[] = ['min', 'peek', 'full'];
    const fromIdx = order.indexOf(drawerStateRef.current);
    const candidates = [fromIdx, fromIdx - 1, fromIdx + 1].filter(
      (i) => i >= 0 && i < order.length
    );
    let best = order[fromIdx];
    let bestDist = Infinity;
    for (const i of candidates) {
      const state = order[i];
      const h = heightForState(state, snaps);
      const dist = Math.abs(h - height);
      if (dist < bestDist) {
        bestDist = dist;
        best = state;
      }
    }
    applyState(best);
  };

  const isDragHandleTarget = (target: EventTarget | null) => {
    if (!(target instanceof Element)) return false;
    return Boolean(target.closest(`.${style.header}`));
  };

  const handleSheetPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragHandleTarget(e.target)) return;
    if (e.button !== 0) return;

    pointerOnHeaderRef.current = true;
    pointerStartRef.current = { x: e.clientX, y: e.clientY };
    draggedRef.current = false;
    isDraggingRef.current = false;
    setIsDragging(false);
    dragStartYRef.current = e.clientY;
    dragStartHeightRef.current =
      dragHeightPx ??
      heightForState(drawerStateRef.current, snapHeightsRef.current);
    sheetRef.current?.setPointerCapture(e.pointerId);
  };

  const handleSheetPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const start = pointerStartRef.current;
    if (!start) return;

    const moved = Math.hypot(e.clientX - start.x, e.clientY - start.y);
    if (moved <= DRAG_THRESHOLD_PX) return;

    draggedRef.current = true;
    if (!isDraggingRef.current) {
      isDraggingRef.current = true;
      setIsDragging(true);
    }

    const snaps = snapHeightsRef.current;
    const dy = dragStartYRef.current - e.clientY;
    const next = Math.min(
      Math.max(dragStartHeightRef.current + dy, snaps.indicator),
      snaps.full
    );
    setDragHeightPx(next);
    setExpandCover(expandCoverForHeight(next, snaps.peek, snaps.full));
  };

  const endSheetPointer = (e: React.PointerEvent<HTMLDivElement>) => {
    const sheet = sheetRef.current;
    if (sheet?.hasPointerCapture(e.pointerId)) {
      sheet.releasePointerCapture(e.pointerId);
    }
    const startedOnHeader = pointerOnHeaderRef.current;
    pointerOnHeaderRef.current = false;
    pointerStartRef.current = null;

    if (isDraggingRef.current) {
      e.preventDefault();
      e.stopPropagation();
      suppressClickRef.current = true;
      const height =
        dragHeightPx ??
        heightForState(drawerStateRef.current, snapHeightsRef.current);
      settleFromHeight(height);
      isDraggingRef.current = false;
      setIsDragging(false);
      draggedRef.current = false;
      return;
    }

    if (!startedOnHeader) {
      draggedRef.current = false;
      return;
    }

    draggedRef.current = false;
    applyState(isExpanded ? 'peek' : 'full');
  };

  const collapse = () => applyState('peek');

  const ctaClass =
    style.cta + (firstExpandThisSession ? ' ' + style.ctaBreathing : '');

  const renderBanner = () => {
    if (status === 'thanks') {
      return (
        <div className={style.banner} role="status" aria-live="polite">
          <p className={style.bannerHeadline}>
            {t('befriend.thanks', 'Thank you for befriending.')}
          </p>
        </div>
      );
    }

    if (status === 'rate_limited') {
      const secs = Math.max(
        1,
        Math.ceil((errorInfo?.retryAfterMs ?? 30000) / 1000)
      );
      const dayLimit = secs > 60 * 5;
      return (
        <div className={style.banner} role="status" aria-live="polite">
          <p className={style.bannerHeadline}>
            {dayLimit
              ? t(
                  'befriend.dailyLimit',
                  "You've reached today's befriend limit. Try again tomorrow."
                )
              : t(
                  'befriend.cooldown',
                  'Please wait {secs} seconds before reporting another befriend.',
                  { secs }
                )}
          </p>
        </div>
      );
    }

    if (status === 'error') {
      return (
        <div className={style.banner} role="status" aria-live="polite">
          <p className={style.bannerHeadline}>
            {t('befriend.errorHeadline', 'Couldn’t record your befriend.')}
          </p>
          <p className={style.bannerSubline}>
            {errorInfo?.message ??
              t('befriend.errorGeneric', 'Please try again.')}
          </p>
        </div>
      );
    }

    return null;
  };

  if (!container) {
    return null;
  }

  const sheetClass =
    style.sheet + (isDragging ? ' ' + style.sheetDragging : '');
  const portalClass =
    style.portalRoot + (isDragging ? ' ' + style.portalDragging : '');

  return createPortal(
    <div className={portalClass}>
      {isDragging && <div className={style.dragShield} aria-hidden />}
      {expandCover > 0 && (
        <div
          className={style.scrim}
          aria-hidden
          style={{ opacity: expandCover * 0.45 }}
        />
      )}
      <div
        ref={sheetRef}
        className={sheetClass}
        style={{ height: settledHeight }}
        role="dialog"
        aria-label={t('befriend.sheetTitle', 'Befriending')}
        onPointerDown={handleSheetPointerDown}
        onPointerMove={handleSheetPointerMove}
        onPointerUp={endSheetPointer}
        onPointerCancel={endSheetPointer}
      >
        <div
          ref={innerRef}
          className={
            style.inner + (drawerState === 'min' ? ' ' + style.innerMin : '')
          }
          data-state={drawerState}
          data-show-body={showBody ? '' : undefined}
          data-scroll-body={bodyScrollNeeded ? '' : undefined}
        >
          <button
            ref={headerRef}
            type="button"
            className={style.header}
            aria-expanded={isExpanded}
            aria-label={
              isExpanded
                ? t('befriend.collapseAria', 'Collapse Befriend sheet')
                : t('befriend.expandAria', 'Expand Befriend sheet')
            }
          >
            <div ref={handleRef} className={style.handle} aria-hidden="true" />
            <h2 className={style.title}>
              {t('befriend.title', 'Befriending')}
            </h2>
          </button>

          {!showBody && drawerState !== 'min' && (
            <div className={style.peekFill} aria-hidden="true" />
          )}

          <div ref={bodyRef} className={style.body}>
            {renderBanner()}

            {(status === 'idle' || status === 'submitting') && (
              <>
                <p className={style.prompt}>
                  {t(
                    'befriend.prompt',
                    'Befriend another person by asking them a question about their life.'
                  )}
                </p>

                {counts && (
                  <div className={style.counts}>
                    <div className={style.countCell}>
                      <span className={style.countNumber}>
                        {counts.totalToday}
                      </span>
                      <span className={style.countLabel}>
                        {t('befriend.todayLabel', 'People Befriended')}
                      </span>
                    </div>
                    <div className={style.countCell}>
                      <span className={style.countNumber}>
                        {counts.totalWorldwide.toLocaleString()}
                      </span>
                      <span className={style.countLabel}>
                        {t('befriend.worldLabel', 'Worldwide')}
                      </span>
                    </div>
                  </div>
                )}

                <button
                  ref={ctaRef}
                  type="button"
                  className={ctaClass}
                  onClick={handleTap}
                  disabled={status !== 'idle'}
                >
                  {status === 'submitting'
                    ? t('befriend.submitting', 'Recording…')
                    : t('befriend.cta', 'I just befriended someone')}
                </button>

                <button
                  type="button"
                  className={style.dismiss}
                  onClick={collapse}
                >
                  {t('befriend.maybeLater', 'Maybe later')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    container
  );
}
